"use client";

import { EmailExists } from "@/components/forms/already-exists";
import { VerifyEmail } from "@/components/forms/verify-email";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { signUp } from "@/lib/auth/auth-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const signUpSchema = z.object({
  username: z.string().min(3, "Username must be at least 3 characters"),
  email: z.string().email("Please enter a valid email address"),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(
      /[^A-Za-z0-9]/,
      "Password must contain at least one special character"
    ),
});

type SignUpValues = z.infer<typeof signUpSchema>;

type SignUpFormProps = {
  isBusiness?: boolean;
};

export function SignUpForm({ isBusiness = false }: SignUpFormProps) {
  const [error, setError] = useState<string>();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [emailExists, setEmailExists] = useState(false);

  const form = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      username: "",
      email: "",
      password: "",
    },
  });

  async function onSubmit({ email, password, username }: SignUpValues) {
    try {
      setError(undefined);
      const res = await signUp.email({
        email,
        password,
        name: username,
        role: isBusiness ? "business" : "user",
        callbackURL: isBusiness ? "/dashboard/setup" : "/",
      });
      if (res.error?.code === "USER_ALREADY_EXISTS") {
        setEmailExists(true);
      }

      if (!res.data?.user.id) {
        setError("Something went wrong. Please try again.");
        return;
      }

      setHasSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    }
  }

  if (hasSubmitted) {
    return (
      <>
        {emailExists ? (
          <EmailExists email={form.getValues("email")} />
        ) : (
          <VerifyEmail email={form.getValues("email")} />
        )}
        <Button
          variant="ghost"
          onClick={() => setHasSubmitted(false)}
          className="group"
        >
          <ArrowLeft className="size-4 transition-transform group-hover:-translate-x-1" />
          Back to sign up
        </Button>
      </>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Create an account</CardTitle>
          <CardDescription>
            Enter your details below to create your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Username</FormLabel>
                      <FormControl>
                        <Input placeholder="johndoe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="john@example.com"
                          type="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="••••••••"
                          type="password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {error && <div className="text-destructive text-sm">{error}</div>}
              <Button
                type="submit"
                className="w-full"
                disabled={form.formState.isSubmitting}
              >
                Sign up
              </Button>
              <div className="text-center text-sm">
                Already have an account?{" "}
                <Link
                  href="/user-flow"
                  className="underline underline-offset-4"
                >
                  Login
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
      <Link
        href="/sign-up"
        className="text-muted-foreground text-center text-sm underline"
      >
        Not a business? Sign up as a regular user
      </Link>
    </>
  );
}
